import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/auth/AuthContext'

const API = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

const DOMAINS = [
  { id: 'physical-motor',    label: 'Physical & Motor',    short: 'Physical',   icon: '🏃', color: '#1B8A5A', bg: '#EDF7F2', border: '#52BE80' },
  { id: 'socio-emotional',   label: 'Socio-Emotional',     short: 'Socio-Emot', icon: '💛', color: '#C0392B', bg: '#FDEDEC', border: '#E57373' },
  { id: 'cognitive',         label: 'Cognitive',           short: 'Cognitive',  icon: '🧠', color: '#7B2D8B', bg: '#F5EEF8', border: '#A569BD' },
  { id: 'language-literacy', label: 'Language & Literacy', short: 'Language',   icon: '📚', color: '#003366', bg: '#E8F0FE', border: '#4A90D9' },
  { id: 'numeracy',          label: 'Numeracy',            short: 'Numeracy',   icon: '🔢', color: '#F57F17', bg: '#FFFDE7', border: '#FFD54F' },
]

const ALL_INDICATORS = [
  { id:'pm1', domainId:'physical-motor',    label:'Holds pencil/crayon with control'               },
  { id:'pm2', domainId:'physical-motor',    label:'Cuts along a line with scissors'                },
  { id:'pm3', domainId:'physical-motor',    label:'Strings beads or laces shoes'                   },
  { id:'pm4', domainId:'physical-motor',    label:'Draws basic shapes (circle, square)'            },
  { id:'pm5', domainId:'physical-motor',    label:'Walks, runs, jumps with coordination'           },
  { id:'pm6', domainId:'physical-motor',    label:'Catches and throws a ball'                      },
  { id:'pm7', domainId:'physical-motor',    label:'Balances on one foot briefly'                   },
  { id:'pm8', domainId:'physical-motor',    label:'Climbs, crawls, rolls with confidence'          },
  { id:'se1', domainId:'socio-emotional',   label:'Identifies and names basic emotions'            },
  { id:'se2', domainId:'socio-emotional',   label:'Regulates strong emotions with support'         },
  { id:'se3', domainId:'socio-emotional',   label:'Shares and takes turns with peers'              },
  { id:'se4', domainId:'socio-emotional',   label:'Shows empathy toward others'                    },
  { id:'se5', domainId:'socio-emotional',   label:'Separates from caregiver without distress'      },
  { id:'se6', domainId:'socio-emotional',   label:'Participates in group activities'               },
  { id:'se7', domainId:'socio-emotional',   label:'Follows simple classroom rules'                 },
  { id:'se8', domainId:'socio-emotional',   label:'Expresses needs and wants clearly'              },
  { id:'cg1', domainId:'cognitive',         label:'Sorts objects by color, shape, size'            },
  { id:'cg2', domainId:'cognitive',         label:'Identifies patterns and continues them'         },
  { id:'cg3', domainId:'cognitive',         label:'Solves simple cause-and-effect problems'        },
  { id:'cg4', domainId:'cognitive',         label:'Matches and categorizes objects'                },
  { id:'cg5', domainId:'cognitive',         label:'Sequences events in correct order'              },
  { id:'cg6', domainId:'cognitive',         label:'Demonstrates memory for recent events'          },
  { id:'cg7', domainId:'cognitive',         label:'Completes simple puzzles (4–8 pieces)'          },
  { id:'cg8', domainId:'cognitive',         label:'Shows curiosity and asks questions'             },
  { id:'ll1', domainId:'language-literacy', label:'Listens and responds to simple instructions'    },
  { id:'ll2', domainId:'language-literacy', label:'Speaks in 3–5 word sentences'                  },
  { id:'ll3', domainId:'language-literacy', label:'Recognizes and names letters (mother tongue)'   },
  { id:'ll4', domainId:'language-literacy', label:'Identifies beginning sounds of words'           },
  { id:'ll5', domainId:'language-literacy', label:'Retells a story with key details'               },
  { id:'ll6', domainId:'language-literacy', label:'Shows book-handling behaviour (left-right)'     },
  { id:'ll7', domainId:'language-literacy', label:'Engages in conversation with turns'             },
  { id:'ll8', domainId:'language-literacy', label:'Recognizes own name in print'                   },
  { id:'nm1', domainId:'numeracy',          label:'Counts objects 1–10 with 1-to-1 correspondence' },
  { id:'nm2', domainId:'numeracy',          label:'Recognizes numerals 1–10'                       },
  { id:'nm3', domainId:'numeracy',          label:'Compares quantities (more, less, equal)'        },
  { id:'nm4', domainId:'numeracy',          label:'Identifies basic shapes in environment'         },
  { id:'nm5', domainId:'numeracy',          label:'Uses positional language (above, below, beside)'},
  { id:'nm6', domainId:'numeracy',          label:'Completes simple AB/ABC patterns'               },
  { id:'nm7', domainId:'numeracy',          label:'Understands concept of addition (adding more)'  },
  { id:'nm8', domainId:'numeracy',          label:'Sorts and groups by number'                     },
]

const LEVELS = {
  'not-observed': { short:'N/O', color:'#9E9E9E', bg:'#F5F5F5', score:0, label:'Not Observed' },
  'emerging':     { short:'E',   color:'#E53935', bg:'#FFEBEE', score:1, label:'Emerging'     },
  'developing':   { short:'D',   color:'#F9A825', bg:'#FFF9C4', score:2, label:'Developing'   },
  'achieved':     { short:'A',   color:'#2E7D32', bg:'#E8F5E9', score:3, label:'Achieved'     },
}

const MODE_ICON = {
  flashcard:'🃏', flip:'🃏', quiz:'🎯', match:'🧩',
  worksheet:'📝', activitycard:'🖼️', storybook:'📖',
  workplan:'📅',
}
// ─── Helpers ──────────────────────────────────────────────────────────────────
function flattenRecord(apiRecord) {
  if (!apiRecord?.indicators) return {}
  const out = {}
  for (const ind of apiRecord.indicators) {
    out[`${ind.domainId}::${ind.indicatorId}`] = {
      level: ind.current?.level || 'not-observed',
      note:  ind.current?.note  || '',
    }
  }
  return out
}

function domainPct(record = {}, domainId) {
  const keys = Object.keys(record).filter(k => k.startsWith(domainId + '::'))
  if (!keys.length) return 0
  return Math.round(keys.reduce((s, k) => s + (LEVELS[record[k]?.level]?.score ?? 0), 0) / (keys.length * 3) * 100)
}

function overallPct(record = {}) {
  const scores = DOMAINS.map(d => domainPct(record, d.id))
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
}

function calcAge(dob) {
  if (!dob) return null
  const d = new Date(dob), now = new Date()
  const y = now.getFullYear() - d.getFullYear()
  return now.getMonth() < d.getMonth() ? y - 1 : y
}

function relDate(iso) {
  if (!iso) return '—'
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (d === 0) return 'Today'
  if (d === 1) return 'Yesterday'
  if (d < 7)   return `${d}d ago`
  if (d < 30)  return `${Math.floor(d / 7)}w ago`
  return new Date(iso).toLocaleDateString('en-IN', { day:'numeric', month:'short' })
}

function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
}

function accColor(pct) {
  if (pct == null) return '#9E9E9E'
  return pct >= 70 ? '#1B6B45' : pct >= 40 ? '#D4AC0D' : '#C0392B'
}

function accBg(pct) {
  if (pct == null) return '#F5F5F5'
  return pct >= 70 ? '#F0FFF4' : pct >= 40 ? '#FFFDE7' : '#FFEBEE'
}

function deriveLevel(sessions = []) {
  const rated = sessions.filter(s => s.ratedCount > 0)
  if (!rated.length) return null
  const avg = Math.round(rated.reduce((a, s) => a + (s.correctCount || 0) / s.ratedCount, 0) / rated.length * 100)
  const label = avg >= 80 ? 'Advanced' : avg >= 60 ? 'Developing' : avg >= 40 ? 'Emerging' : 'Foundation'
  let spw = sessions.length
  if (sessions.length >= 2) {
    const span = new Date(sessions[0].createdAt) - new Date(sessions[sessions.length - 1].createdAt)
    spw = Math.round(sessions.length / Math.max(1, span / (7 * 864e5)) * 10) / 10
  }
  return { avg, label, color: accColor(avg), spw }
}

function sessAcc(s) {
  return s?.ratedCount > 0 ? Math.round((s.correctCount || 0) / s.ratedCount * 100) : null
}

// ─── Next-week plan helper ─────────────────────────────────────────────────────
function getNextMondayISO() {
  const d = new Date()
  const day = d.getDay()                  // 0=Sun…6=Sat
  const diff = day === 0 ? 1 : 8 - day   // days until next Mon
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d.toISOString().split('T')[0]
}

// ─── UpcomingWeekReminder ─────────────────────────────────────────────────────
// Shown in overview tab when one or more children don't have a plan for next week
function UpcomingWeekReminder({ childrenWithoutPlan, onNav }) {
  const [dismissed, setDismissed] = useState(false)
  if (!childrenWithoutPlan.length || dismissed) return null
  const count = childrenWithoutPlan.length
  const names = childrenWithoutPlan.slice(0, 3).map(c => c.name.split(' ')[0]).join(', ')
  const more  = count > 3 ? ` +${count - 3} more` : ''
  return (
    <div style={{
      background: 'linear-gradient(130deg, #78350F 0%, #B45309 100%)',
      borderRadius: 14, padding: '16px 22px', marginBottom: 20,
      display: 'flex', alignItems: 'center', gap: 18, color: 'white',
      boxShadow: '0 4px 20px rgba(180,83,9,.3)',
    }}>
      <span style={{ fontSize: 36, flexShrink: 0, lineHeight: 1 }}>📋</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 16, fontWeight: 900, marginBottom: 5 }}>
          Next week's plan isn't set yet
        </div>
        <div style={{ fontSize: 12, lineHeight: 1.7, opacity: .88 }}>
          {count === 1
            ? `${names} doesn't have a work plan for next week.`
            : `${count} children have no plan for next week — ${names}${more}.`}
          {' '}Parents can't see what's coming until a plan is created.
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button onClick={() => {
            sessionStorage.setItem('workplan_target_week', getNextMondayISO())
            onNav('workplan')
          }}
          style={{ background: 'white', border: 'none', color: '#B45309', borderRadius: 9,
                   padding: '9px 18px', fontWeight: 800, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          📅 Create Plan
        </button>
        <button onClick={() => setDismissed(true)}
          style={{ background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.35)', color: 'white',
                   borderRadius: 9, padding: '9px 12px', fontSize: 12, cursor: 'pointer' }}>
          ✕
        </button>
      </div>
    </div>
  )
}

// ─── Tiny atoms ───────────────────────────────────────────────────────────────
function Bar({ pct, color, h = 6 }) {
  return (
    <div style={{ height: h, background: '#ECF0F5', borderRadius: h, overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color, borderRadius: h, transition: 'width .5s ease' }} />
    </div>
  )
}

function Pill({ label, color, bg, size = 10 }) {
  return (
    <span style={{ display: 'inline-block', background: bg || `${color}18`, color, fontSize: size,
                   fontWeight: 700, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap', letterSpacing: .2 }}>
      {label}
    </span>
  )
}

function Avatar({ name = '', color = '#1A4A6E', size = 36, ring }) {
  const circ = 2 * Math.PI * ((size + 8 - 4) / 2)
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      {ring != null && (
        <svg width={size + 8} height={size + 8} style={{ position:'absolute', top:-4, left:-4, transform:'rotate(-90deg)', pointerEvents:'none' }}>
          <circle cx={(size+8)/2} cy={(size+8)/2} r={(size+8-4)/2} fill="none" stroke="#E4ECF5" strokeWidth={3.5} />
          <circle cx={(size+8)/2} cy={(size+8)/2} r={(size+8-4)/2} fill="none" stroke={color} strokeWidth={3.5}
            strokeDasharray={circ} strokeDashoffset={circ - ring / 100 * circ} strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset .7s ease' }} />
        </svg>
      )}
      <div style={{ width: size, height: size, borderRadius: '50%', background: color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 900, fontSize: size * 0.34, letterSpacing: -.5 }}>
        {initials(name)}
      </div>
    </div>
  )
}

function Spinner({ size = 20, color = 'var(--navy)' }) {
  return (
    <div style={{ width: size, height: size, border: `3px solid ${color}22`,
                  borderTop: `3px solid ${color}`, borderRadius: '50%', animation: 'td-spin .7s linear infinite' }} />
  )
}

function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign: 'center', padding: '28px 0', color: '#B0BECC' }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 12, fontWeight: 600 }}>{text}</div>
    </div>
  )
}

// ─── Algorithm Data Panel ─────────────────────────────────────────────────────
function AlgoPanel({ algData, firstName }) {
  if (!algData) {
    return (
      <div style={{ background: '#F8FAFD', border: '1.5px solid #E4ECF5', borderRadius: 12, padding: '20px 18px',
                    display: 'flex', alignItems: 'center', gap: 10, color: '#9EB0C4' }}>
        <Spinner size={16} color="#4A90D9" />
        <span style={{ fontSize: 12, fontWeight: 600 }}>Loading algorithm data…</span>
      </div>
    )
  }

  const { queue, sessions } = algData
  const totalDue  = queue?.totalDue ?? 0
  const groups    = queue?.queue    ?? []
  const lastSess  = sessions?.[0]
  const lastAcc   = sessAcc(lastSess)
  const caught    = totalDue === 0

  return (
    <div style={{ background: '#F8FAFD', border: '1.5px solid #E4ECF5', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #E8EFF7', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14 }}>🧠</span>
        <span style={{ fontSize: 10, fontWeight: 800, color: '#7A95B2', textTransform: 'uppercase', letterSpacing: .8 }}>
          Algorithm Data · SM-2 · BKT · Elo
        </span>
      </div>
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          <div style={{ borderRadius: 10, padding: '10px 12px', textAlign: 'center',
                        background: caught ? '#F0FFF4' : '#FFF4EC',
                        border: `1.5px solid ${caught ? '#A8E6C0' : '#FFB347'}` }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: caught ? '#1B6B45' : '#E65100', lineHeight: 1, marginBottom: 3 }}>
              {caught ? '✓' : totalDue}
            </div>
            <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .6,
                          color: caught ? '#1B6B45' : '#E65100' }}>
              {caught ? 'Caught up' : 'Cards Due'}
            </div>
          </div>
          <div style={{ borderRadius: 10, padding: '10px 12px', textAlign: 'center', background: '#EBF4FB', border: '1.5px solid #BDD7EE' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#003366', lineHeight: 1, marginBottom: 3 }}>
              {sessions?.length ?? 0}
            </div>
            <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .6, color: '#1A4A6E' }}>Sessions</div>
          </div>
          <div style={{ borderRadius: 10, padding: '10px 12px', textAlign: 'center',
                        background: accBg(lastAcc), border: `1.5px solid ${accColor(lastAcc)}40` }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: accColor(lastAcc), lineHeight: 1, marginBottom: 3 }}>
              {lastAcc != null ? `${lastAcc}%` : '—'}
            </div>
            <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .6, color: accColor(lastAcc) }}>Last Acc.</div>
          </div>
        </div>
        {groups.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#7A95B2', textTransform: 'uppercase', letterSpacing: .7, marginBottom: 8 }}>Due by Set</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {groups.map((g, i) => {
                const mp = Math.round((g.masteryAvg || 0) * 100)
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                                         background: 'white', borderRadius: 8, border: '1px solid #E8EFF7' }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>🃏</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#1A2E4A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {g.setTitle || 'Untitled Set'}
                      </div>
                      <div style={{ marginTop: 3 }}><Bar pct={mp} color={accColor(mp)} h={3} /></div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                      <Pill label={`⏰ ${g.dueCount}`} color="#E65100" bg="#FFF4EC" size={9} />
                      <Pill label={`M: ${mp}%`} color={accColor(mp)} size={9} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {sessions?.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#7A95B2', textTransform: 'uppercase', letterSpacing: .7, marginBottom: 8 }}>Recent Sessions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {sessions.slice(0, 5).map((s, i) => {
                const acc   = sessAcc(s)
                const mIcon = MODE_ICON[s.moduleType] || '📌'
                const dateStr = s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : '—'
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 10px',
                                         background: 'white', borderRadius: 8, border: '1px solid #E8EFF7' }}>
                    <span style={{ fontSize: 15, width: 22, textAlign: 'center', flexShrink: 0 }}>{mIcon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#1A2E4A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.setTitle || s.moduleType || 'Session'}
                      </div>
                      <div style={{ fontSize: 9, color: '#8FA5BC', marginTop: 1 }}>
                        {dateStr} · {s.ratedCount ?? s.cardCount ?? 0} cards
                      </div>
                    </div>
                    {acc != null && <Pill label={`${acc}%`} color={accColor(acc)} bg={accBg(acc)} size={10} />}
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {!groups.length && !sessions?.length && (
          <div style={{ textAlign: 'center', padding: '8px 0 4px', fontSize: 11, color: '#9EB0C4' }}>
            No flashcard sessions yet for {firstName}.
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Child Pace + Level Summary Card ─────────────────────────────────────────
function ChildLevelCard({ child, sessions, totalDue, overall, onClick }) {
  const level = deriveLevel(sessions)
  const age   = calcAge(child.dob)
  const lastS = sessions[0]?.createdAt
  return (
    <div onClick={onClick}
      style={{ background: 'white', borderRadius: 13, border: '1.5px solid #E4ECF5',
               borderLeft: `4px solid ${child.color || '#1A4A6E'}`, padding: '14px 16px',
               cursor: 'pointer', transition: 'box-shadow .15s, transform .12s' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,40,100,.1)'; e.currentTarget.style.transform = 'translateY(-1px)' }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = '' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 11 }}>
        <Avatar name={child.name} color={child.color || '#1A4A6E'} size={40} ring={overall} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 13.5, color: '#1A2E4A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{child.name}</div>
          <div style={{ fontSize: 10, color: '#8FA5BC', marginTop: 2 }}>
            {age != null ? `Age ${age}` : '—'}
            {lastS ? ` · last ${relDate(lastS)}` : ''}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 21, fontWeight: 900, color: child.color || '#1A4A6E', lineHeight: 1 }}>{overall}%</div>
          <div style={{ fontSize: 9, color: '#8FA5BC', fontWeight: 600 }}>NCF overall</div>
        </div>
      </div>
      {level ? (
        <div style={{ display: 'flex', gap: 7, marginBottom: 10 }}>
          <div style={{ flex: 1, background: `${level.color}10`, border: `1px solid ${level.color}28`, borderRadius: 8, padding: '7px 10px' }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: level.color }}>{level.label}</div>
            <div style={{ fontSize: 9, color: '#8FA5BC', marginTop: 1 }}>{level.avg}% avg accuracy</div>
          </div>
          <div style={{ background: '#EBF4FB', border: '1px solid #BDD7EE', borderRadius: 8, padding: '7px 12px', textAlign: 'center', minWidth: 56 }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#003366', lineHeight: 1 }}>{level.spw}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#1A4A6E', marginTop: 2 }}>sess/wk</div>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 11, color: '#9EB0C4', fontStyle: 'italic', marginBottom: 10 }}>No sessions yet</div>
      )}
      {totalDue > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#FFF4EC', border: '1px solid #FFB347', borderRadius: 7, padding: '5px 10px' }}>
          <span style={{ fontSize: 12 }}>⏰</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#E65100' }}>{totalDue} card{totalDue !== 1 ? 's' : ''} due for review</span>
        </div>
      ) : sessions.length > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F0FFF4', border: '1px solid #A8E6C0', borderRadius: 7, padding: '5px 10px' }}>
          <span style={{ fontSize: 12 }}>✅</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#1B6B45' }}>Caught up — great retention!</span>
        </div>
      ) : null}
    </div>
  )
}



// ─── SessionHistoryList ───────────────────────────────────────────────────────
// Filterable session list used in Session History tab
// Answers Q2: "Show only teacher-led flashcard sessions"
function SessionHistoryList({ allSess }) {
  const [filter, setFilter] = useState('all')  // 'all' | 'teacher' | 'parent'

  const filtered = filter === 'all'     ? allSess
    : filter === 'parent'  ? allSess.filter(s => s.conductorRole === 'parent')
    :                        allSess.filter(s => s.conductorRole !== 'parent')

  const shownSess = filtered.slice(0, 30)

  return (
    <div style={{ background:'white', borderRadius:14, padding:'19px 21px', border:'1.5px solid #E4ECF5' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <div>
          <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:15, fontWeight:800, color:'#1A2E4A' }}>Session History</div>
          <div style={{ fontSize:11, color:'#8FA5BC', marginTop:2 }}>All children · last {shownSess.length}</div>
        </div>
      </div>
      {/* Filter */}
      <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
        {[['all','All Sessions'],['teacher','🏫 School Only'],['parent','🏠 Home Only']].map(([v,l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{
            padding:'5px 12px', borderRadius:7, fontWeight:700, fontSize:11, cursor:'pointer',
            border:`1.5px solid ${filter===v?'#1A2E4A':'#D9E4F0'}`,
            background:filter===v?'#1A2E4A':'white', color:filter===v?'white':'#3C4F66',
          }}>{l}</button>
        ))}
        <span style={{ fontSize:11, color:'#8FA5BC', alignSelf:'center', marginLeft:'auto' }}>
          {filtered.length} session{filtered.length!==1?'s':''}
        </span>
      </div>
      {/* List */}
      {shownSess.length === 0 ? <EmptyState icon="📋" text="No sessions for this filter" /> : (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {shownSess.map(s => {
            const acc  = sessAcc(s)
            const role = s.conductorRole || 'teacher'
            const isParent = role === 'parent'
            const roleLbl  = isParent ? '🏠 Home' : '🏫 School'
            const roleCol  = isParent ? '#7B2D8B' : '#1A4A6E'
            const roleBg   = isParent ? '#F5F0FF' : '#EBF4FB'
            return (
              <div key={s._id} style={{ display:'flex', alignItems:'center', gap:10, background:'#F8FAFD', borderRadius:8, padding:'9px 12px' }}>
                <span style={{ fontSize:16, flexShrink:0 }}>{MODE_ICON[s.moduleType]||'📌'}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#1A2E4A', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {s.setTitle || s.moduleType || 'Session'}
                  </div>
                  <div style={{ fontSize:10, color:'#8FA5BC', marginTop:1, display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                    <strong style={{ color:s.childColor }}>{s.childName}</strong>
                    {s.durationMin && <span>{s.durationMin} min</span>}
                    <span>{relDate(s.createdAt)}</span>
                    <span style={{ fontSize:9, fontWeight:700, color:roleCol, background:roleBg, padding:'1px 6px', borderRadius:5 }}>{roleLbl}</span>
                  </div>
                </div>
                {acc != null && <Pill label={`${acc}%`} color={accColor(acc)} bg={accBg(acc)} size={10} />}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── ClassBKTTable ────────────────────────────────────────────────────────────
// Class-level flashcard mastery matrix: rows = flashcard sets, cols = children
// Answers Q5: "Class-level mastery for all children?"
function ClassBKTTable({ children, algoResults }) {
  const [expandedSet, setExpandedSet] = useState(null)

  if (!algoResults || Object.keys(algoResults).length === 0) return null

  // Collect all unique sets across children
  const setMap = {}
  children.forEach(c => {
    ;(algoResults[c._id] || []).forEach(r => {
      const key = String(r.flashcardSet || r.setTitle || 'x')
      if (!setMap[key]) setMap[key] = { setTitle: r.setTitle || 'Untitled', rows: {} }
      setMap[key].rows[c._id] = r
    })
  })
  const sets = Object.entries(setMap)
  if (!sets.length) return null

  const PCOLOR = {
    review:    '#E65100',
    practice:  '#7B2D8B',
    reinforce: '#1D4ED8',
    maintain:  '#16A34A',
    none:      '#9E9E9E',
  }
  const PBG = {
    review:    '#FFF4EC',
    practice:  '#F5F0FF',
    reinforce: '#EFF6FF',
    maintain:  '#F0FFF4',
    none:      '#F5F5F5',
  }
  const PICON = { review:'⏰', practice:'🎯', reinforce:'💪', maintain:'✅', none:'—' }

  return (
    <div style={{ background:'white', borderRadius:14, border:'1.5px solid #E4ECF5', overflow:'hidden' }}>
      {/* Header */}
      <div style={{ padding:'16px 20px', borderBottom:'1px solid #E4ECF5', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:15, fontWeight:800, color:'#1A2E4A' }}>
            🧠 Class Flashcard Mastery
          </div>
          <div style={{ fontSize:11, color:'#8FA5BC', marginTop:2 }}>
            BKT algorithm mastery per child per set · updated after each session
          </div>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {[['#E65100','⏰ Review Due'],['#7B2D8B','🎯 Practice'],['#1D4ED8','💪 Reinforce'],['#16A34A','✅ Mastered']].map(([c,l])=>(
            <div key={l} style={{ display:'flex', alignItems:'center', gap:4 }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:c, flexShrink:0 }}/>
              <span style={{ fontSize:9, color:'#7A95B2', fontWeight:600 }}>{l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX:'auto' }}>
        <table style={{ borderCollapse:'collapse', width:'100%', minWidth:400 }}>
          <thead>
            <tr style={{ background:'#F8FAFD' }}>
              <th style={{ textAlign:'left', padding:'8px 16px', fontSize:10, color:'#7A95B2', fontWeight:700, minWidth:180, position:'sticky', left:0, background:'#F8FAFD', zIndex:2 }}>
                Flashcard Set
              </th>
              {children.map(c => (
                <th key={c._id} style={{ padding:'6px 10px', minWidth:72, textAlign:'center', borderLeft:'1px solid #F0F5FC' }}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                    <div style={{ width:28, height:28, borderRadius:'50%', background:c.color||'#1A4A6E', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:900, fontSize:11 }}>
                      {c.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                    </div>
                    <span style={{ fontSize:9, color:'#7A95B2', maxWidth:70, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:600 }}>
                      {c.name.split(' ')[0]}
                    </span>
                  </div>
                </th>
              ))}
              <th style={{ padding:'6px 10px', minWidth:60, textAlign:'center', borderLeft:'1px solid #F0F5FC', fontSize:10, color:'#7A95B2', fontWeight:700 }}>
                Class Avg
              </th>
            </tr>
          </thead>
          <tbody>
            {sets.map(([key, {setTitle, rows}]) => {
              const expanded = expandedSet === key
              const childResults  = children.map(c => rows[c._id]).filter(Boolean)
              const classAvgMastery = childResults.length
                ? Math.round(childResults.reduce((s,r) => s + (r.avgMasteryProb||0), 0) / childResults.length * 100)
                : null
              const totalDueClass = childResults.reduce((s,r) => s + (r.dueCount||0), 0)

              return (
                <>
                  <tr key={key} style={{ borderTop:'1px solid #F0F5FC', cursor:'pointer' }}
                    onClick={() => setExpandedSet(expanded ? null : key)}
                    onMouseEnter={e=>e.currentTarget.style.background='#F8FAFD'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}>
                    <td style={{ padding:'10px 16px', position:'sticky', left:0, background:'inherit', zIndex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                        <span style={{ fontSize:13 }}>🃏</span>
                        <div>
                          <div style={{ fontSize:12, fontWeight:700, color:'#1A2E4A' }}>
                            {setTitle.length > 28 ? setTitle.slice(0,28)+'…' : setTitle}
                          </div>
                          {totalDueClass > 0 && (
                            <div style={{ fontSize:9, color:'#E65100', fontWeight:700 }}>⏰ {totalDueClass} card{totalDueClass!==1?'s':''} due across class</div>
                          )}
                        </div>
                        <span style={{ fontSize:10, color:'#C0CDD8', marginLeft:'auto' }}>{expanded?'▲':'▼'}</span>
                      </div>
                    </td>
                    {children.map(c => {
                      const r = rows[c._id]
                      if (!r) return (
                        <td key={c._id} style={{ textAlign:'center', padding:'8px 10px', borderLeft:'1px solid #F0F5FC' }}>
                          <span style={{ fontSize:10, color:'#D1D5DB' }}>—</span>
                        </td>
                      )
                      const mp  = Math.round((r.avgMasteryProb||0)*100)
                      const col = PCOLOR[r.priority]||PCOLOR.none
                      const bg  = PBG[r.priority]||PBG.none
                      const icon= PICON[r.priority]||'—'
                      return (
                        <td key={c._id} style={{ textAlign:'center', padding:'6px 10px', borderLeft:'1px solid #F0F5FC' }}>
                          <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', gap:2 }}>
                            <div style={{ fontSize:13, fontWeight:900, color:col }}>{mp}%</div>
                            <div style={{ fontSize:9, fontWeight:700, color:col, background:bg, padding:'1px 6px', borderRadius:6, whiteSpace:'nowrap' }}>
                              {icon} {r.priority==='none'?'—':r.priority}
                            </div>
                            {r.dueCount > 0 && (
                              <div style={{ fontSize:8, color:'#E65100', fontWeight:700 }}>⏰ {r.dueCount} due</div>
                            )}
                          </div>
                        </td>
                      )
                    })}
                    <td style={{ textAlign:'center', padding:'6px 10px', borderLeft:'1px solid #F0F5FC' }}>
                      {classAvgMastery != null ? (
                        <div style={{ fontSize:14, fontWeight:900, color: classAvgMastery>=70?'#16A34A':classAvgMastery>=40?'#D4AC0D':'#C0392B' }}>
                          {classAvgMastery}%
                        </div>
                      ) : <span style={{ fontSize:10, color:'#D1D5DB' }}>—</span>}
                    </td>
                  </tr>
                  {/* Expanded: per-card details */}
                  {expanded && (
                    <tr key={key+'-exp'} style={{ background:'#F8FAFD', borderTop:'1px solid #F0F5FC' }}>
                      <td colSpan={children.length + 2} style={{ padding:'12px 16px' }}>
                        <div style={{ fontSize:10, fontWeight:800, color:'#7A95B2', textTransform:'uppercase', letterSpacing:.8, marginBottom:8 }}>
                          Card-level BKT mastery breakdown
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                          {children.map(c => {
                            const r = rows[c._id]
                            if (!r || !r.cards?.length) return null
                            return (
                              <div key={c._id}>
                                <div style={{ fontSize:11, fontWeight:700, color:c.color||'#1A4A6E', marginBottom:4 }}>{c.name}</div>
                                <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                                  {r.cards.slice(0,10).map((card,i) => {
                                    const mp = Math.round((card.masteryProb||0)*100)
                                    const col = mp>=80?'#16A34A':mp>=50?'#D4AC0D':'#C0392B'
                                    return (
                                      <div key={i} style={{ background:'white', border:`1.5px solid ${col}40`, borderRadius:7, padding:'4px 8px', fontSize:9, fontWeight:700, color:col }}>
                                        {card.cardLabel||`Card ${i+1}`}: {mp}%
                                        {card.dueDate && new Date(card.dueDate)<=new Date() && <span style={{ color:'#E65100' }}> ⏰</span>}
                                      </div>
                                    )
                                  })}
                                  {r.cards.length > 10 && <span style={{ fontSize:9, color:'#9EB0C4', alignSelf:'center' }}>+{r.cards.length-10} more</span>}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Schedule Tab ─────────────────────────────────────────────────────────────
function ScheduleTab({ children, sessions, records, reviewQueues, algoResults, currentPlan, todayDayIdx, onNav, onOpenChild }) {
  const allLoaded = children.every(c => reviewQueues[c._id] !== undefined)

  // Derive today's WorkPlan pending entries from props
  const wpTodayEntries = currentPlan
    ? (currentPlan.days?.find(d => d.dayIndex === todayDayIdx)?.entries || [])
    : []
  const wpTodayPending = wpTodayEntries.filter(e => e.status !== 'done').length
  const wpTodayTotal   = wpTodayEntries.length

  const dueGroups = children.flatMap(c => {
    const q = reviewQueues[c._id]
    if (!q?.totalDue) return []
    return (q.queue || []).map(g => ({ ...g, child: c }))
  }).sort((a, b) => (b.dueCount || 0) - (a.dueCount || 0))
  const totalDueAll = dueGroups.reduce((s, g) => s + (g.dueCount || 0), 0)
  const childrenDue = new Set(dueGroups.map(g => g.child._id)).size
  const caughtUp    = children.filter(c => reviewQueues[c._id] && !reviewQueues[c._id]?.totalDue).length
  const suggestions = children
    .map(c => ({ c, q: reviewQueues[c._id], sess: sessions[c._id] || [] }))
    .filter(x => x.q?.totalDue > 0)
    .sort((a, b) => (b.q?.totalDue || 0) - (a.q?.totalDue || 0))

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* ── Combined hero banner: SM-2 + WorkPlan ── */}
        {(() => {
          const combinedDue = totalDueAll + wpTodayPending
          const noData = Object.values(sessions).flat().length === 0 && wpTodayTotal === 0
          const allDone = combinedDue === 0 && !noData
          const bg = combinedDue > 0
            ? 'linear-gradient(130deg,#C0392B 0%,#E65100 100%)'
            : noData
              ? 'linear-gradient(130deg,#1A2E4A 0%,#2C4A6E 100%)'
              : 'linear-gradient(130deg,#1B6B45 0%,#27AE60 100%)'
          const subtitle = combinedDue > 0
            ? [
                totalDueAll > 0   && `⏰ ${totalDueAll} SM-2 card${totalDueAll!==1?'s':''} due`,
                wpTodayPending > 0 && `📅 ${wpTodayPending} plan activit${wpTodayPending!==1?'ies':'y'} pending`,
              ].filter(Boolean).join('  ·  ')
            : noData
              ? 'Log flashcard sessions and create a work plan to activate scheduling'
              : `All ${children.length} children on track · ${wpTodayTotal > 0 ? `${wpTodayTotal - wpTodayPending}/${wpTodayTotal} plan activities done` : 'No plan activities for today'}`
          return (
            <div style={{ background:bg, borderRadius:16, padding:'22px 26px', color:'white', display:'flex', alignItems:'center', gap:18 }}>
              <div style={{ fontSize:44, lineHeight:1, flexShrink:0 }}>
                {combinedDue > 0 ? '⏰' : noData ? '📋' : '✅'}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:22, fontWeight:900, marginBottom:5, lineHeight:1.2 }}>
                  {combinedDue > 0
                    ? `${combinedDue} item${combinedDue!==1?'s':''} due today`
                    : noData ? 'No sessions or activities yet' : 'Class is fully caught up!'}
                </div>
                <div style={{ fontSize:12, opacity:.85, lineHeight:1.5 }}>{subtitle}</div>
                {/* Mini pill breakdown */}
                {combinedDue > 0 && (
                  <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap' }}>
                    {totalDueAll > 0 && (
                      <div style={{ background:'rgba(255,255,255,.15)', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>
                        🃏 {totalDueAll} SM-2 due
                      </div>
                    )}
                    {wpTodayPending > 0 && (
                      <div style={{ background:'rgba(255,255,255,.15)', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>
                        📅 {wpTodayPending}/{wpTodayTotal} plan pending
                      </div>
                    )}
                  </div>
                )}
              </div>
              {(totalDueAll > 0 || wpTodayPending > 0) && (
                <div style={{ display:'flex', flexDirection:'column', gap:6, flexShrink:0 }}>
                  {totalDueAll > 0 && (
                    <button onClick={() => onNav('learn')} style={{ background:'rgba(255,255,255,.2)', border:'1.5px solid rgba(255,255,255,.5)', color:'white', borderRadius:9, padding:'8px 16px', fontWeight:700, fontSize:11, cursor:'pointer', whiteSpace:'nowrap' }}>
                      🎮 Start Session
                    </button>
                  )}
                  {wpTodayPending > 0 && (
                    <button onClick={() => onNav('workplan')} style={{ background:'rgba(255,255,255,.15)', border:'1px solid rgba(255,255,255,.35)', color:'white', borderRadius:9, padding:'8px 16px', fontWeight:700, fontSize:11, cursor:'pointer', whiteSpace:'nowrap' }}>
                      📅 Open Work Plan
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })()}
        {/* ── Today's WorkPlan Activities ── */}
        {(() => {
          const todayEntries = currentPlan
            ? (currentPlan.days?.find(d => d.dayIndex === todayDayIdx)?.entries || [])
            : []
          const pending = todayEntries.filter(e => e.status !== 'done')
          const done    = todayEntries.filter(e => e.status === 'done')
          const total   = todayEntries.length
          const pct     = total > 0 ? Math.round(done.length / total * 100) : 0
          const MODULE_ICON = { flashcard:'🃏', worksheet:'📝', activitycard:'🖼️', storybook:'📖', custom:'✨', 'learning-activity':'🎯' }
          const DAY_LABELS  = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday']
          const todayLabel  = DAY_LABELS[todayDayIdx] || 'Today'

          return (
            <div style={{ background:'white', borderRadius:14, border:'1.5px solid #E4ECF5', overflow:'hidden' }}>
              <div style={{ padding:'14px 18px 12px', borderBottom:'1px solid #F0F5FC',
                display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:15, fontWeight:800, color:'#1A2E4A' }}>
                    📅 {todayLabel}'s WorkPlan
                  </div>
                  <div style={{ fontSize:11, color:'#8FA5BC', marginTop:2 }}>
                    {currentPlan ? currentPlan.title || 'Weekly Work Plan' : 'No plan for this week'}
                    {total > 0 && ` · ${done.length}/${total} done`}
                  </div>
                </div>
                {total > 0 && (
                  <div style={{ textAlign:'center', flexShrink:0 }}>
                    <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:22, fontWeight:900,
                      color: pct===100?'#1B6B45':pending.length>0?'#E65100':'#1A2E4A', lineHeight:1 }}>{pct}%</div>
                    <div style={{ fontSize:9, color:'#8FA5BC', marginTop:1 }}>complete</div>
                  </div>
                )}
              </div>

              {!currentPlan ? (
                <div style={{ padding:'20px 18px', textAlign:'center', color:'#9EB0C4' }}>
                  <div style={{ fontSize:28, marginBottom:8 }}>📋</div>
                  <div style={{ fontSize:12, fontWeight:600, marginBottom:4 }}>No work plan for this week</div>
                  <button onClick={() => onNav('workplan')} style={{ fontSize:11, fontWeight:700, color:'#1A4A6E',
                    background:'#EBF4FB', border:'1px solid #BDD7EE', borderRadius:7, padding:'5px 12px', cursor:'pointer', marginTop:4 }}>
                    📅 Create Plan →
                  </button>
                </div>
              ) : total === 0 ? (
                <div style={{ padding:'16px 18px', textAlign:'center', color:'#9EB0C4' }}>
                  <div style={{ fontSize:22, marginBottom:6 }}>📅</div>
                  <div style={{ fontSize:12, fontWeight:600 }}>No activities planned for {todayLabel}</div>
                </div>
              ) : (
                <div style={{ padding:'12px 18px' }}>
                  {/* Progress bar */}
                  {total > 0 && (
                    <div style={{ marginBottom:12 }}>
                      <div style={{ height:6, background:'#F0F5FC', borderRadius:4, overflow:'hidden' }}>
                        <div style={{ width:`${pct}%`, height:'100%',
                          background: pct===100?'#1B6B45':'#EA580C', borderRadius:4, transition:'width .5s' }}/>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', marginTop:4, fontSize:10, color:'#8FA5BC' }}>
                        <span>{done.length} completed</span>
                        <span>{pending.length} remaining</span>
                      </div>
                    </div>
                  )}
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {todayEntries.map((e, i) => {
                      const isDone = e.status === 'done'
                      const icon = MODULE_ICON[e.moduleType] || '📌'
                      return (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:10,
                          padding:'9px 12px', borderRadius:9,
                          background: isDone ? '#F0FFF4' : '#FAFCFF',
                          border: `1px solid ${isDone ? '#A8E6C0' : '#E8EFF7'}`,
                          borderLeft: `3px solid ${isDone ? '#1B6B45' : '#EA580C'}` }}>
                          <span style={{ fontSize:16, flexShrink:0 }}>{icon}</span>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:12, fontWeight:700,
                              color: isDone ? '#1B6B45' : '#1A2E4A',
                              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                              textDecoration: isDone ? 'line-through' : 'none', opacity: isDone ? 0.7 : 1 }}>
                              {e.contentTitle || e.goal || 'Untitled'}
                            </div>
                            <div style={{ fontSize:10, color:'#8FA5BC', marginTop:1, display:'flex', gap:6 }}>
                              {e.durationMin && <span>⏱ {e.durationMin} min</span>}
                              {e.domain && <span style={{ color:'#7A95B2' }}>{e.domain}</span>}
                            </div>
                          </div>
                          <div style={{ flexShrink:0 }}>
                            {isDone
                              ? <Pill label="✅ Done" color="#1B6B45" bg="#F0FFF4" size={9} />
                              : <Pill label="⏳ Pending" color="#E65100" bg="#FFF4EC" size={9} />}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {pending.length > 0 && (
                    <button onClick={() => onNav('workplan')} style={{ width:'100%', marginTop:10,
                      padding:'7px 0', background:'#F8FAFD', border:'1px solid #D9E4F0', borderRadius:8,
                      fontSize:11, fontWeight:700, color:'#1A4A6E', cursor:'pointer' }}>
                      ✏️ Mark activities in Work Plan →
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })()}

        {/* ── Unified "Due Today" card: SM-2 + WorkPlan side by side ── */}
        <div style={{ background:'white', borderRadius:14, border:'1.5px solid #E4ECF5', overflow:'hidden' }}>
          {/* Header */}
          <div style={{ padding:'14px 18px 12px', borderBottom:'1px solid #F0F5FC', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:15, fontWeight:800, color:'#1A2E4A' }}>
              Due Today
            </div>
            <div style={{ display:'flex', gap:7 }}>
              {totalDueAll > 0    && <Pill label={`🃏 ${totalDueAll} SM-2`}    color="#E65100" bg="#FFF4EC" size={10} />}
              {wpTodayPending > 0 && <Pill label={`📅 ${wpTodayPending} plan`} color="#1A4A6E" bg="#EBF4FB" size={10} />}
              {totalDueAll === 0 && wpTodayPending === 0 && <Pill label="✅ All clear" color="#1B6B45" bg="#F0FFF4" size={10} />}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>

            {/* ── Left: SM-2 Flashcard Due ── */}
            <div style={{ padding:'14px 16px', borderRight:'1px solid #F0F5FC' }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:10 }}>
                <span style={{ fontSize:15 }}>🃏</span>
                <div>
                  <div style={{ fontSize:12, fontWeight:800, color:'#1A2E4A' }}>SM-2 Flashcard Reviews</div>
                  <div style={{ fontSize:10, color:'#8FA5BC', marginTop:1 }}>
                    Algorithm-scheduled · spaced repetition
                  </div>
                </div>
              </div>
              {!allLoaded ? (
                <div style={{ display:'flex', justifyContent:'center', padding:16 }}><Spinner /></div>
              ) : dueGroups.length === 0 ? (
                <div style={{ textAlign:'center', padding:'14px 8px' }}>
                  <div style={{ fontSize:24, marginBottom:6 }}>
                    {Object.values(sessions).flat().length === 0 ? '🃏' : '✅'}
                  </div>
                  <div style={{ fontSize:11, fontWeight:600, color:'#9EB0C4', lineHeight:1.5 }}>
                    {Object.values(sessions).flat().length === 0
                      ? <>No flashcard sessions logged yet.<br/>Log a session to activate SM-2.</>
                      : <>All caught up!<br/>No cards due today.</>}
                  </div>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {dueGroups.map((g, i) => {
                    const mp = Math.round((g.masteryAvg || 0) * 100)
                    return (
                      <div key={i} onClick={() => onOpenChild(g.child)}
                        style={{ display:'flex', alignItems:'center', gap:9, padding:'8px 10px',
                          background:'#FAFCFF', borderRadius:9, border:'1px solid #E8EFF7', cursor:'pointer' }}
                        onMouseEnter={e=>e.currentTarget.style.background='#F0F5FC'}
                        onMouseLeave={e=>e.currentTarget.style.background='#FAFCFF'}>
                        <Avatar name={g.child.name} color={g.child.color||'#1A4A6E'} size={30} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:11, fontWeight:800, color:'#1A2E4A',
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.child.name}</div>
                          <div style={{ fontSize:10, color:'#8FA5BC',
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.setTitle||'Untitled set'}</div>
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:3, alignItems:'flex-end', flexShrink:0 }}>
                          <Pill label={`⏰ ${g.dueCount} cards`} color="#E65100" bg="#FFF4EC" size={9} />
                          <Pill label={`BKT ${mp}%`}            color={accColor(mp)} size={9} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* ── Right: WorkPlan Activities Due ── */}
            <div style={{ padding:'14px 16px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:10 }}>
                <span style={{ fontSize:15 }}>📅</span>
                <div>
                  <div style={{ fontSize:12, fontWeight:800, color:'#1A2E4A' }}>Today's Plan Activities</div>
                  <div style={{ fontSize:10, color:'#8FA5BC', marginTop:1 }}>
                    From work plan · mark done in planner
                  </div>
                </div>
              </div>
              {!currentPlan ? (
                <div style={{ textAlign:'center', padding:'14px 8px' }}>
                  <div style={{ fontSize:24, marginBottom:6 }}>📋</div>
                  <div style={{ fontSize:11, fontWeight:600, color:'#9EB0C4', lineHeight:1.5 }}>
                    No work plan for this week.<br/>
                    <button onClick={()=>onNav('workplan')} style={{ fontSize:10, fontWeight:700,
                      color:'#1A4A6E', background:'#EBF4FB', border:'1px solid #BDD7EE',
                      borderRadius:6, padding:'3px 9px', cursor:'pointer', marginTop:4 }}>
                      📅 Create Plan
                    </button>
                  </div>
                </div>
              ) : wpTodayTotal === 0 ? (
                <div style={{ textAlign:'center', padding:'14px 8px' }}>
                  <div style={{ fontSize:24, marginBottom:6 }}>📅</div>
                  <div style={{ fontSize:11, fontWeight:600, color:'#9EB0C4' }}>
                    No activities planned for today
                  </div>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {/* Mini progress bar */}
                  <div style={{ marginBottom:4 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'#8FA5BC', marginBottom:3 }}>
                      <span>{wpTodayTotal - wpTodayPending} done</span>
                      <span>{wpTodayPending} pending</span>
                    </div>
                    <div style={{ height:5, background:'#F0F5FC', borderRadius:3, overflow:'hidden' }}>
                      <div style={{ width:`${Math.round((wpTodayTotal-wpTodayPending)/wpTodayTotal*100)}%`,
                        height:'100%', background: wpTodayPending===0?'#1B6B45':'#EA580C',
                        borderRadius:3, transition:'width .4s' }}/>
                    </div>
                  </div>
                  {wpTodayEntries.map((e, i) => {
                    const isDone = e.status === 'done'
                    const MODULE_ICON = { flashcard:'🃏', worksheet:'📝', activitycard:'🖼️', storybook:'📖', custom:'✨', 'learning-activity':'🎯' }
                    const icon = MODULE_ICON[e.moduleType] || '📌'
                    return (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:8,
                        padding:'7px 10px', borderRadius:8,
                        background: isDone?'#F0FFF4':'#FAFCFF',
                        border:`1px solid ${isDone?'#A8E6C0':'#E8EFF7'}`,
                        borderLeft:`3px solid ${isDone?'#1B6B45':'#EA580C'}`,
                        opacity: isDone ? 0.75 : 1 }}>
                        <span style={{ fontSize:14, flexShrink:0 }}>{icon}</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:11, fontWeight:700,
                            color: isDone?'#1B6B45':'#1A2E4A',
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                            textDecoration: isDone?'line-through':'none' }}>
                            {e.contentTitle || e.goal || 'Untitled'}
                          </div>
                          {e.durationMin && (
                            <div style={{ fontSize:9, color:'#8FA5BC', marginTop:1 }}>⏱ {e.durationMin} min</div>
                          )}
                        </div>
                        <Pill label={isDone?'✅ Done':'⏳ Pending'}
                          color={isDone?'#1B6B45':'#E65100'}
                          bg={isDone?'#F0FFF4':'#FFF4EC'} size={9} />
                      </div>
                    )
                  })}
                  <button onClick={()=>onNav('workplan')} style={{ width:'100%', marginTop:2,
                    padding:'6px 0', background:'#F8FAFD', border:'1px solid #D9E4F0',
                    borderRadius:7, fontSize:10, fontWeight:700, color:'#1A4A6E', cursor:'pointer' }}>
                    ✏️ Mark done in Work Plan →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ background: 'white', borderRadius: 14, padding: '18px 20px', border: '1.5px solid #E4ECF5' }}>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 15, fontWeight: 800, color: '#1A2E4A', marginBottom: 4 }}>Child Pace & Level Summary</div>
          <div style={{ fontSize: 11, color: '#8FA5BC', marginBottom: 14 }}>SM-2 status + learning velocity per child</div>
          {children.length === 0 ? <EmptyState icon="👤" text="No children assigned" /> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 10 }}>
              {children.map(c => (
                <ChildLevelCard key={c._id} child={c} sessions={sessions[c._id] || []}
                  totalDue={reviewQueues[c._id]?.totalDue || 0}
                  overall={overallPct(records[c._id] || {})} onClick={() => onOpenChild(c)} />
              ))}
            </div>
          )}
        </div>

        {/* ── Class BKT Mastery Table (Q5: class-level algorithm mastery) ── */}
        <ClassBKTTable children={children} algoResults={algoResults} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ background: 'white', borderRadius: 14, padding: '16px 18px', border: '1.5px solid #E4ECF5' }}>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 14, fontWeight: 800, color: '#1A2E4A', marginBottom: 4 }}>📋 Suggested Sessions</div>
          <div style={{ fontSize: 11, color: '#8FA5BC', marginBottom: 12, lineHeight: 1.6 }}>Children with overdue SM-2 cards — prioritise these today.</div>
          {!allLoaded ? <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}><Spinner /></div>
          : suggestions.length === 0 ? (
              <EmptyState icon={Object.values(sessions).flat().length === 0 ? "🃏" : "🎉"}
                text={Object.values(sessions).flat().length === 0
                  ? "No sessions logged yet — start a flashcard session to activate SM-2 scheduling"
                  : "No sessions needed today!"} />
            )
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {suggestions.map(({ c, q, sess }) => {
                const level = deriveLevel(sess)
                const avgMastery = q.queue?.length ? Math.round(q.queue.reduce((a, g) => a + (g.masteryAvg || 0), 0) / q.queue.length * 100) : null
                return (
                  <div key={c._id} onClick={() => onOpenChild(c)} style={{ background: '#FAFCFF', borderRadius: 10, padding: '10px 12px', border: '1px solid #E8EFF7', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F0F5FC'}
                    onMouseLeave={e => e.currentTarget.style.background = '#FAFCFF'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 7 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.color || '#1A4A6E', flexShrink: 0 }} />
                      <div style={{ fontWeight: 800, fontSize: 12.5, color: '#1A2E4A', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                      <Pill label={`${q.totalDue} due`} color="#E65100" bg="#FFF4EC" size={10} />
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {level && <Pill label={level.label} color={level.color} size={9} />}
                      {avgMastery != null && <Pill label={`BKT ${avgMastery}%`} color={accColor(avgMastery)} size={9} />}
                      {level && <Pill label={`${level.spw}/wk`} color="#003366" bg="#EBF4FB" size={9} />}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <div style={{ background: 'white', borderRadius: 14, padding: '16px 18px', border: '1.5px solid #E4ECF5' }}>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 14, fontWeight: 800, color: '#1A2E4A', marginBottom: 12 }}>🏃 All Children Status</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {children.map(c => {
              const level = deriveLevel(sessions[c._id] || [])
              const due   = reviewQueues[c._id]?.totalDue || 0
              return (
                <div key={c._id} onClick={() => onOpenChild(c)} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 10px', background: '#FAFCFF', borderRadius: 9, border: '1px solid #E8EFF7', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F0F5FC'}
                  onMouseLeave={e => e.currentTarget.style.background = '#FAFCFF'}>
                  <Avatar name={c.name} color={c.color || '#1A4A6E'} size={26} />
                  <div style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 700, color: '#1A2E4A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                    {level ? <Pill label={level.label} color={level.color} size={9} /> : <span style={{ fontSize: 9, color: '#9EB0C4' }}>No data</span>}
                    {due > 0 ? <Pill label={`⏰${due}`} color="#E65100" bg="#FFF4EC" size={9} />
                    : sessions[c._id]?.length > 0 && <Pill label="✓" color="#1B6B45" bg="#F0FFF4" size={9} />}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Class Heatmap ────────────────────────────────────────────────────────────
function ClassHeatmap({ children, records }) {
  const [domain, setDomain] = useState(DOMAINS[0].id)
  const d    = DOMAINS.find(x => x.id === domain)
  const inds = ALL_INDICATORS.filter(i => i.domainId === domain)
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {DOMAINS.map(dd => (
          <button key={dd.id} onClick={() => setDomain(dd.id)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 13px', background: domain === dd.id ? dd.color : 'white', color: domain === dd.id ? 'white' : '#3C4F66', border: `1.5px solid ${domain === dd.id ? dd.color : '#D9E4F0'}`, borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
            <span>{dd.icon}</span><span>{dd.short}</span>
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        {Object.values(LEVELS).map(l => (
          <div key={l.short} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 14, height: 14, borderRadius: 3, background: l.bg, border: `1.5px solid ${l.color}` }} />
            <span style={{ fontSize: 10, color: '#7A95B2', fontWeight: 600 }}>{l.short} = {l.label}</span>
          </div>
        ))}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 500 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, color: '#7A95B2', fontWeight: 700, minWidth: 220, position: 'sticky', left: 0, background: 'white', zIndex: 2 }}>Indicator</th>
              {children.map(c => (
                <th key={c._id} style={{ padding: '4px 5px', minWidth: 54, textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    <Avatar name={c.name} color={c.color || '#1A4A6E'} size={24} />
                    <span style={{ fontSize: 8, color: '#7A95B2', maxWidth: 50, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name.split(' ')[0]}</span>
                  </div>
                </th>
              ))}
              <th style={{ padding: '4px 8px', fontSize: 9, color: '#7A95B2', fontWeight: 700, textAlign: 'center', minWidth: 52 }}>Avg</th>
            </tr>
          </thead>
          <tbody>
            {inds.map(ind => {
              const scores = children.map(c => LEVELS[records[c._id]?.[`${domain}::${ind.id}`]?.level]?.score ?? 0)
              const avg    = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0
              const ap     = Math.round((avg / 3) * 100)
              return (
                <tr key={ind.id} style={{ borderTop: '1px solid #EEF3FA' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFD'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '7px 10px', fontSize: 11, color: '#3C4F66', lineHeight: 1.4, position: 'sticky', left: 0, background: 'inherit', zIndex: 1 }}>
                    <span style={{ fontSize: 9, color: '#9EB0C4', fontWeight: 700, marginRight: 5 }}>{ind.id.toUpperCase()}</span>
                    {ind.label}
                  </td>
                  {children.map(c => {
                    const lv  = records[c._id]?.[`${domain}::${ind.id}`]?.level || 'not-observed'
                    const lvl = LEVELS[lv]
                    return (
                      <td key={c._id} style={{ textAlign: 'center', padding: '5px 5px' }}>
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: lvl.bg, border: `1.5px solid ${lvl.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', fontSize: 10, fontWeight: 900, color: lvl.color }}>
                          {lvl.short}
                        </div>
                      </td>
                    )
                  })}
                  <td style={{ textAlign: 'center', padding: '5px 8px' }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: ap >= 70 ? '#2E7D32' : ap >= 40 ? '#F9A825' : '#E53935' }}>{ap}%</span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── IEP Report Modal ─────────────────────────────────────────────────────────
function IEPModal({ child, record, onClose }) {
  const op  = overallPct(record)
  const age = calcAge(child.dob)
  const domains = DOMAINS.map(d => {
    const pct  = domainPct(record, d.id)
    const ach  = ALL_INDICATORS.filter(i => i.domainId === d.id && record[`${d.id}::${i.id}`]?.level === 'achieved').map(i => i.label)
    const weak = ALL_INDICATORS.filter(i => i.domainId === d.id && (LEVELS[record[`${d.id}::${i.id}`]?.level]?.score ?? 0) <= 1).map(i => i.label)
    return { ...d, pct, ach, weak }
  })
  const print = () => {
    const w = window.open('', '_blank')
    w.document.write(`<html><head><title>IEP — ${child.name}</title>
      <style>body{font-family:Georgia,serif;margin:40px;color:#1a1a2e;font-size:13px}h2{font-size:14px;margin:18px 0 6px;border-bottom:1px solid #ccc;padding-bottom:4px}</style></head>
      <body>${document.getElementById('iep-body').innerHTML}</body></html>`)
    w.document.close(); w.print()
  }
  return (
    <div style={{ position:'fixed', inset:0, zIndex:950, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(10,20,45,.55)', backdropFilter:'blur(3px)' }}>
      <div style={{ background:'white', borderRadius:16, width:660, maxHeight:'88vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px rgba(0,0,0,.25)', overflow:'hidden' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'15px 22px', borderBottom:'1px solid #E4ECF5' }}>
          <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:15, fontWeight:800, color:'#1A2E4A' }}>📄 IEP Report — {child.name}</div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={print} style={{ padding:'7px 14px', background:'#1A2E4A', color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:11, cursor:'pointer' }}>🖨️ Print / PDF</button>
            <button onClick={onClose} style={{ padding:'7px 11px', background:'#EEF3FA', border:'none', borderRadius:8, fontWeight:700, fontSize:11, cursor:'pointer', color:'#3C4F66' }}>✕</button>
          </div>
        </div>
        <div style={{ overflowY:'auto', padding:'20px 24px' }} id="iep-body">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:18 }}>
            <div style={{ background:'#F8FAFD', borderRadius:10, padding:'12px 15px' }}>
              <div style={{ fontSize:9, fontWeight:800, color:'#7A95B2', textTransform:'uppercase', letterSpacing:.8, marginBottom:8 }}>Student Info</div>
              <div style={{ fontSize:12, lineHeight:2, color:'#3C4F66' }}>
                <div><strong>Name:</strong> {child.name}</div>
                {age != null && <div><strong>Age:</strong> {age} years</div>}
                <div><strong>Disabilities:</strong> {(child.disabilities||[]).join(', ')||'—'}</div>
              </div>
            </div>
            <div style={{ background:'#EBF4FB', borderRadius:10, padding:'12px 15px', borderLeft:'3px solid #4A90D9' }}>
              <div style={{ fontSize:9, fontWeight:800, color:'#003366', textTransform:'uppercase', letterSpacing:.8, marginBottom:8 }}>Overall Progress</div>
              <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:34, fontWeight:900, color:'#003366', marginBottom:6 }}>{op}%</div>
              <Bar pct={op} color="#003366" h={7} />
            </div>
          </div>
          {domains.map(d => (
            <div key={d.id} style={{ background:d.bg, borderRadius:10, padding:'11px 14px', marginBottom:10, border:`1px solid ${d.border}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                <span style={{ fontSize:15 }}>{d.icon}</span>
                <span style={{ fontWeight:800, fontSize:13, color:d.color, flex:1 }}>{d.label}</span>
                <span style={{ fontWeight:900, fontSize:14, color:d.color }}>{d.pct}%</span>
              </div>
              <Bar pct={d.pct} color={d.color} h={5} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:9 }}>
                {d.ach.length > 0 && <div><div style={{ fontSize:10, fontWeight:700, color:'#2E7D32', marginBottom:4 }}>✅ Achieved</div>{d.ach.map(l=><div key={l} style={{fontSize:10.5,color:'#1B5E20',marginBottom:2,paddingLeft:8}}>• {l}</div>)}</div>}
                {d.weak.length > 0 && <div><div style={{ fontSize:10, fontWeight:700, color:'#C0392B', marginBottom:4 }}>🎯 Needs Support</div>{d.weak.map(l=><div key={l} style={{fontSize:10.5,color:'#7B241C',marginBottom:2,paddingLeft:8}}>• {l}</div>)}</div>}
              </div>
            </div>
          ))}
          <div style={{ fontSize:10, color:'#9EB0C4', textAlign:'center', paddingTop:12, borderTop:'1px solid #E4ECF5' }}>
            Generated by NISH FSLP · NCF-FS 2022 · {new Date().toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Child Drawer ─────────────────────────────────────────────────────────────
function ChildDrawer({ child, record, srtSessions, algData, algoResults, onClose, onNav }) {
  const op       = overallPct(record)
  const weakInds = ALL_INDICATORS.filter(ind => (LEVELS[record?.[`${ind.domainId}::${ind.id}`]?.level]?.score ?? 0) <= 1)
  return (
    <div style={{ position:'fixed', inset:0, zIndex:900, display:'flex', justifyContent:'flex-end' }}>
      <div onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(10,20,45,.45)', backdropFilter:'blur(2px)' }} />
      <div style={{ position:'relative', zIndex:1, width:500, background:'white', height:'100vh', boxShadow:'-10px 0 50px rgba(0,0,0,.18)', display:'flex', flexDirection:'column', overflowY:'auto', animation:'td-slideIn .22s ease' }}>
        <div style={{ background:'linear-gradient(160deg,#0D2240 0%,#1A4A6E 100%)', padding:'22px 24px 20px', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16 }}>
            <Avatar name={child.name} color={child.color || '#4A90D9'} size={54} ring={op} />
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:20, fontWeight:900, color:'white', marginBottom:4, lineHeight:1.2 }}>{child.name}</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                {calcAge(child.dob) != null && <span style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,.75)', background:'rgba(255,255,255,.12)', padding:'2px 8px', borderRadius:20 }}>Age {calcAge(child.dob)}</span>}
                {(child.disabilities||[]).map(d => <span key={d} style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,.75)', background:'rgba(255,255,255,.12)', padding:'2px 8px', borderRadius:20 }}>{d}</span>)}
              </div>
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,.12)', border:'none', color:'white', borderRadius:'50%', width:30, height:30, cursor:'pointer', fontSize:14, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6 }}>
            {DOMAINS.map(d => {
              const pct = domainPct(record, d.id)
              return (
                <div key={d.id} style={{ background:'rgba(255,255,255,.09)', borderRadius:8, padding:'7px 5px', textAlign:'center' }}>
                  <div style={{ fontSize:14, marginBottom:3 }}>{d.icon}</div>
                  <div style={{ fontSize:13, fontWeight:900, color:'white', lineHeight:1 }}>{pct}%</div>
                  <div style={{ fontSize:8, color:'rgba(255,255,255,.45)', marginTop:2 }}>{d.short}</div>
                </div>
              )
            })}
          </div>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'18px 22px', display:'flex', flexDirection:'column', gap:16 }}>
          <AlgoPanel algData={algData} firstName={child.name.split(' ')[0]} />
          {child.iepGoals && (
            <div style={{ background:'#EBF4FB', borderRadius:10, padding:'12px 14px', borderLeft:'3px solid #4A90D9' }}>
              <div style={{ fontSize:9, fontWeight:800, color:'#003366', textTransform:'uppercase', letterSpacing:.8, marginBottom:5 }}>IEP Goals</div>
              <div style={{ fontSize:12, color:'#3C4F66', lineHeight:1.7 }}>{child.iepGoals}</div>
            </div>
          )}
          {/* Algorithm suggestions for this child */}
          {algoResults && algoResults.length > 0 && (
            <div style={{ background:'#F8FAFD', border:'1.5px solid #E4ECF5', borderRadius:12, overflow:'hidden' }}>
              <div style={{ padding:'10px 16px', borderBottom:'1px solid #E8EFF7', display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:14 }}>🎯</span>
                <span style={{ fontSize:10, fontWeight:800, color:'#7A95B2', textTransform:'uppercase', letterSpacing:.8 }}>Flashcard Suggestions — Algorithm Ranked</span>
              </div>
              <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:6 }}>
                {algoResults.slice(0,6).map((r, i) => {
                  const mp = Math.round((r.avgMasteryProb||0)*100)
                  const PSTYLE = {
                    review:    { color:'#E65100', bg:'#FFF4EC', border:'#FFB347', icon:'⏰', label:'Review Due' },
                    practice:  { color:'#7B2D8B', bg:'#F5F0FF', border:'#C4B5FD', icon:'🎯', label:'Needs Practice' },
                    reinforce: { color:'#1D4ED8', bg:'#EFF6FF', border:'#93C5FD', icon:'💪', label:'Reinforce' },
                    maintain:  { color:'#16A34A', bg:'#F0FFF4', border:'#86EFAC', icon:'✅', label:'Maintain' },
                  }
                  const ps = PSTYLE[r.priority] || PSTYLE.maintain
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 10px', background:'white', borderRadius:8, border:`1px solid ${ps.border}` }}>
                      <span style={{ fontSize:16, flexShrink:0 }}>🃏</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#1A2E4A', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.setTitle||'Untitled Set'}</div>
                        <div style={{ fontSize:9, color:'#8FA5BC', marginTop:1 }}>{r.suggestionNote||''}</div>
                      </div>
                      <div style={{ flexShrink:0, textAlign:'right' }}>
                        <div style={{ fontSize:9, fontWeight:800, color:ps.color, background:ps.bg, padding:'2px 7px', borderRadius:6, marginBottom:2 }}>{ps.icon} {ps.label}</div>
                        <div style={{ fontSize:9, color:'#8FA5BC' }}>BKT {mp}%{r.dueCount>0?` · ${r.dueCount} due`:''}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div>
            <div style={{ fontSize:9, fontWeight:800, color:'#7A95B2', textTransform:'uppercase', letterSpacing:.8, marginBottom:9 }}>
              Focus Areas · {weakInds.length} indicator{weakInds.length !== 1 ? 's' : ''} need attention
            </div>
            {weakInds.length === 0 ? (
              <div style={{ fontSize:12, color:'#1B6B45', fontWeight:700 }}>✅ All indicators at Developing or Achieved</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                {weakInds.slice(0, 7).map(ind => {
                  const lv  = record?.[`${ind.domainId}::${ind.id}`]?.level || 'not-observed'
                  const lvl = LEVELS[lv]
                  const dom = DOMAINS.find(d => d.id === ind.domainId)
                  return (
                    <div key={`${ind.domainId}::${ind.id}`} style={{ display:'flex', alignItems:'center', gap:8, background:'#F8FAFD', borderRadius:8, padding:'7px 10px', border:'1px solid #E8EFF7' }}>
                      <span style={{ fontSize:13 }}>{dom?.icon}</span>
                      <span style={{ flex:1, fontSize:11, color:'#3C4F66', lineHeight:1.4 }}>{ind.label}</span>
                      <span style={{ fontSize:9, fontWeight:800, color:lvl.color, background:lvl.bg, padding:'2px 6px', borderRadius:5 }}>{lvl.short}</span>
                    </div>
                  )
                })}
                {weakInds.length > 7 && <div style={{ fontSize:11, color:'#9EB0C4', textAlign:'center' }}>+{weakInds.length-7} more</div>}
              </div>
            )}
          </div>
          {child.clinicalNotes && (
            <div style={{ background:'#FFFDE7', borderRadius:10, padding:'11px 14px', borderLeft:'3px solid #F9A825' }}>
              <div style={{ fontSize:9, fontWeight:800, color:'#7D6608', textTransform:'uppercase', letterSpacing:.8, marginBottom:5 }}>Clinical Notes</div>
              <div style={{ fontSize:11.5, color:'#3C4F66', lineHeight:1.7 }}>{child.clinicalNotes}</div>
            </div>
          )}
        </div>
        <div style={{ borderTop:'1px solid #E4ECF5', padding:'14px 22px', display:'flex', gap:8, flexShrink:0 }}>
          <button onClick={() => { onNav('learn'); onClose() }} style={{ flex:1, padding:'10px 0', background:'#7B5800', color:'white', border:'none', borderRadius:9, fontWeight:700, fontSize:12, cursor:'pointer' }}>
            🎮 Learning Activity
          </button>
          <button onClick={() => { onNav('progress'); onClose() }} style={{ flex:1, padding:'10px 0', background:'linear-gradient(130deg,#0D2240,#1A4A6E)', color:'white', border:'none', borderRadius:9, fontWeight:700, fontSize:12, cursor:'pointer' }}>
            📊 Update Progress
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function TeacherDashboard({ onNav }) {
  const { user, getAuthHeader } = useAuth()
  const getHdr = getAuthHeader

  const [children,      setChildren]      = useState([])
  const [records,       setRecords]       = useState({})
  const [sessions,      setSessions]      = useState({})
  const [srtSessions,   setSrtSessions]   = useState({})
  const [reviewQueues,  setReviewQueues]  = useState({})
  const [loading,       setLoading]       = useState(true)
  // ── Next-week plan state ── key: childId → null (no plan) | true (has plan)
  const [nextWeekPlans, setNextWeekPlans] = useState({})
  const [nextWeekLoading, setNextWeekLoading] = useState(false)

  const [algData,       setAlgData]       = useState({})
  const [algoResults,   setAlgoResults]   = useState({})  // childId → AlgorithmResult[]
  const [currentPlan,   setCurrentPlan]   = useState(null) // scope=all plan for this week
  const [activeTab,     setActiveTab]     = useState('overview')
  const [selectedChild, setSelectedChild] = useState(null)
  const [reportChild,   setReportChild]   = useState(null)
  const [search,        setSearch]        = useState('')

  // ── Load all data on mount ────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true)
      try {
        const res  = await fetch(`${API}/children`, { headers: getHdr() })
        const data = await res.json()
        const list = data.children || []
        setChildren(list)

        const [recArr, sessArr, srtArr, qArr] = await Promise.all([
          Promise.all(list.map(async c => {
            try { const r = await fetch(`${API}/progress/${c._id}`, { headers: getHdr() }); const d = await r.json(); return [c._id, flattenRecord(d.record)] }
            catch { return [c._id, {}] }
          })),
          Promise.all(list.map(async c => {
            try { const r = await fetch(`${API}/flashcard-sessions/${c._id}?limit=10`, { headers: getHdr() }); const d = await r.json(); return [c._id, d.sessions || []] }
            catch { return [c._id, []] }
          })),
          Promise.all(list.map(async c => {
            try { const r = await fetch(`${API}/srt/${c._id}`, { headers: getHdr() }); const d = await r.json(); return [c._id, d.sessions || []] }
            catch { return [c._id, []] }
          })),
          Promise.all(list.map(async c => {
            try { const r = await fetch(`${API}/flashcard-sessions/${c._id}/review-queue`, { headers: getHdr() }); const d = await r.json(); return [c._id, d] }
            catch { return [c._id, { totalDue: 0, queue: [] }] }
          })),
        ])

        setRecords(Object.fromEntries(recArr))
        setSessions(Object.fromEntries(sessArr))
        setSrtSessions(Object.fromEntries(srtArr))
        setReviewQueues(Object.fromEntries(qArr))

        // ── Fetch AlgorithmResult suggestions per child (non-blocking) ────
        Promise.all(list.map(async c => {
          try {
            const r = await fetch(`${API}/algorithm-results/${c._id}/suggestions?limit=10`, { headers: getHdr() })
            const d = await r.json()
            return [c._id, d.success ? d.suggestions || [] : []]
          } catch { return [c._id, []] }
        })).then(arr => setAlgoResults(Object.fromEntries(arr))).catch(() => {})

        // ── Fetch current week's all-scope work plan for today's activities ──
        try {
          const mon = new Date()
          const day = mon.getDay()
          const diff = day === 0 ? 1 : (1 - day)
          mon.setDate(mon.getDate() + diff)
          mon.setHours(0, 0, 0, 0)
          const monStr = mon.toISOString().split('T')[0]
          const planRes = await fetch(
            `${API}/workplan?weekStart=${monStr}&scope=all&limit=1`,
            { headers: getHdr() }
          ).then(r => r.json()).catch(() => ({ plans: [] }))
          const plans = planRes.plans || planRes.workPlans || []
          if (plans[0]) {
            // Fetch full plan with entries
            const fullRes = await fetch(`${API}/workplan/${plans[0]._id}`, { headers: getHdr() })
              .then(r => r.json()).catch(() => null)
            if (fullRes?.success) setCurrentPlan(fullRes.plan)
          }
        } catch {}

        // ── After main load: check next-week plans for all children ──────
        const nextIso = getNextMondayISO()
        setNextWeekLoading(true)
        try {
          // First: check if a school-wide (scope=all) plan exists for next week
          // If yes, all children are covered by that one plan
          const allPlanRes = await fetch(
            `${API}/workplan?weekStart=${nextIso}&scope=all&limit=1`,
            { headers: getHdr() }
          ).then(r => r.json()).catch(() => ({ plans: [] }))
          const hasAllScopePlan = (allPlanRes.plans || allPlanRes.workPlans || []).length > 0

          // Then check child-specific plans only if no all-scope plan exists
          const nwArr = await Promise.all(list.map(async c => {
            if (hasAllScopePlan) return [c._id, true]  // covered by school-wide plan
            try {
              const r = await fetch(`${API}/workplan/child/${c._id}/week/${nextIso}`, { headers: getHdr() })
              const d = await r.json()
              return [c._id, d.success === true]
            } catch { return [c._id, false] }
          }))
          setNextWeekPlans(Object.fromEntries(nwArr))
        } catch {}
        setNextWeekLoading(false)

      } catch (e) {
        console.error('TeacherDashboard load error', e)
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // ── Fetch algData on child selection ────────────────────────────────────
  useEffect(() => {
    if (!selectedChild) return
    const id = selectedChild._id
    if (algData[id] !== undefined) return
    setAlgData(prev => ({ ...prev, [id]: null }))
    ;(async () => {
      try {
        const [qRes, sRes, arRes] = await Promise.all([
          fetch(`${API}/flashcard-sessions/${id}/review-queue`, { headers: getHdr() })
            .then(r => r.ok ? r.json() : { totalDue: 0, queue: [] }).catch(() => ({ totalDue: 0, queue: [] })),
          fetch(`${API}/flashcard-sessions/${id}?limit=5`, { headers: getHdr() })
            .then(r => r.json()).catch(() => ({ sessions: [] })),
          fetch(`${API}/algorithm-results/${id}/suggestions?limit=10`, { headers: getHdr() })
            .then(r => r.json()).catch(() => ({ success: false, suggestions: [] })),
        ])
        setAlgData(prev => ({ ...prev, [id]: { queue: qRes, sessions: sRes.sessions || [] } }))
        if (arRes.success) setAlgoResults(prev => ({ ...prev, [id]: arRes.suggestions || [] }))
      } catch {
        setAlgData(prev => ({ ...prev, [id]: { queue: { totalDue: 0, queue: [] }, sessions: [] } }))
      }
    })()
  }, [selectedChild])

  // ── Derived ────────────────────────────────────────────────────────────────
  const filtered      = children.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()))
  const totalDueAll   = Object.values(reviewQueues).reduce((s, q) => s + (q?.totalDue || 0), 0)
  const classAvg      = DOMAINS.map(d => ({
    ...d, avg: children.length ? Math.round(children.reduce((s, c) => s + domainPct(records[c._id] || {}, d.id), 0) / children.length) : 0
  }))
  const classOverall  = classAvg.length ? Math.round(classAvg.reduce((s, d) => s + d.avg, 0) / classAvg.length) : 0
  const needAttention = children.map(c => ({ ...c, op: overallPct(records[c._id] || {}) })).filter(c => c.op < 50).sort((a, b) => a.op - b.op)
  const today         = new Date().toDateString()
  const sessToday     = Object.values(sessions).flat().filter(s => new Date(s.createdAt).toDateString() === today).length

  // ── Today's work plan activities ─────────────────────────────────────────
  const todayDayIdx = (() => {
    const d = new Date().getDay() // 0=Sun
    return d === 0 ? 6 : d - 1   // Mon=0…Sat=5
  })()
  const todayPlanEntries = currentPlan
    ? (currentPlan.days?.find(d => d.dayIndex === todayDayIdx)?.entries || [])
    : []
  const todayPending  = todayPlanEntries.filter(e => e.status === 'pending' || e.status !== 'done')
  const todayDone     = todayPlanEntries.filter(e => e.status === 'done')
  const todayTotal    = todayPlanEntries.length

  // Children without a next-week plan (exclude loading state)
  const childrenWithoutNextPlan = !nextWeekLoading
    ? children.filter(c => nextWeekPlans[c._id] === false)
    : []

  const openChild = useCallback(c => setSelectedChild(c), [])

  if (loading) {
    return (
      <div style={{ padding: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, minHeight: 400 }}>
        <Spinner size={28} color="#1A4A6E" />
        <div style={{ fontSize: 13, color: '#8FA5BC', fontWeight: 600 }}>Loading class data…</div>
        <style>{`@keyframes td-spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    )
  }

  return (
    <div style={{ padding: '28px 26px 80px', maxWidth: 1220, margin: '0 auto' }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 28, fontWeight: 900, color: '#1A2E4A', marginBottom: 4, lineHeight: 1 }}>
          Teacher Dashboard
        </div>
        <div style={{ fontSize: 13, color: '#8FA5BC' }}>
          {user?.name || 'Teacher'} · {children.length} assigned children · {new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long' })}
        </div>
      </div>

      {/* ── Stats row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { icon:'👤', val:children.length,       label:'Assigned Children', sub:'active profiles',   color:'#1A4A6E', bg:'white' },
          { icon:'📊', val:`${classOverall}%`,    label:'Class Average',     sub:'NCF all domains',   color:'#1B6B45', bg:'white' },
          { icon:'⚠️', val:needAttention.length,  label:'Need Attention',    sub:'below 50% overall', color:'#C0392B', bg:'white' },
          { icon:'📅', val:sessToday,             label:'Sessions Today',    sub:'logged so far',     color:'#7B2D8B', bg:'white' },
          { icon:'⏰', val: totalDueAll + todayPending.length, label:'Due Today', sub:`${totalDueAll} SM-2 · ${todayPending.length} plan`, color:'#E65100', bg:(totalDueAll + todayPending.length) > 0 ? '#FFF4EC' : 'white', onClick: () => setActiveTab('schedule') },
        ].map((s, i) => (
          <div key={i} onClick={s.onClick}
            style={{ background: s.bg, borderRadius: 13, padding: '16px 18px', border: `1.5px solid ${totalDueAll > 0 && i === 4 ? '#FFB347' : '#E4ECF5'}`, cursor: s.onClick ? 'pointer' : 'default', transition: 'box-shadow .15s' }}
            onMouseEnter={e => s.onClick && (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.09)')}
            onMouseLeave={e => e.currentTarget.style.boxShadow = ''}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
              <span style={{ fontSize:20 }}>{s.icon}</span>
              <span style={{ fontSize:9, color:'#8FA5BC', fontWeight:600, background:'#F0F5FC', padding:'2px 7px', borderRadius:8 }}>{s.sub}</span>
            </div>
            <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:28, fontWeight:900, color:s.color, lineHeight:1, marginBottom:3 }}>{s.val}</div>
            <div style={{ fontSize:12, color:'#7A95B2', fontWeight:600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display:'flex', gap:6, marginBottom:22 }}>
        {[
          { id:'overview', icon:'🏠', label:'Class Overview' },
          { id:'schedule', icon:'⏰', label:'SM-2 Schedule', badge: totalDueAll > 0 ? totalDueAll : null },
          { id:'heatmap',  icon:'🔥', label:'Skills Heatmap' },
          { id:'planner',  icon:'📅', label:'Session History' },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 17px',
                     background: activeTab === t.id ? '#1A2E4A' : 'white',
                     color: activeTab === t.id ? 'white' : '#3C4F66',
                     border: `1.5px solid ${activeTab === t.id ? '#1A2E4A' : '#D9E4F0'}`,
                     borderRadius:9, fontSize:12, fontWeight:700, cursor:'pointer', transition:'all .15s' }}>
            <span>{t.icon}</span><span>{t.label}</span>
            {t.badge && <span style={{ background:'#E65100', color:'white', fontSize:9, fontWeight:900, borderRadius:12, padding:'1px 6px', marginLeft:2 }}>{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* ══ OVERVIEW ══ */}
      {activeTab === 'overview' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 290px', gap:20 }}>
          <div>
            {/* ── Upcoming week reminder banner ── */}
            <UpcomingWeekReminder childrenWithoutPlan={childrenWithoutNextPlan} onNav={onNav} />

            {/* Search + child list */}
            <div style={{ marginBottom:14 }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍  Search children…"
                style={{ width:'100%', padding:'10px 14px', border:'1.5px solid #D9E4F0', borderRadius:9, fontSize:13, color:'#1A2E4A', outline:'none', boxSizing:'border-box' }} />
            </div>
            <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:16, fontWeight:800, color:'#1A2E4A', marginBottom:3 }}>Assigned Children</div>
            <div style={{ fontSize:11, color:'#8FA5BC', marginBottom:13 }}>{filtered.length} of {children.length} shown</div>
            {filtered.length === 0 ? <EmptyState icon="👤" text="No children assigned yet" /> : (
              <div style={{ display:'flex', flexDirection:'column', gap:9 }}>
                {filtered.map(c => {
                  const rec    = records[c._id] || {}
                  const op     = overallPct(rec)
                  const lastS  = sessions[c._id]?.[0]?.createdAt
                  const weakCt = ALL_INDICATORS.filter(ind => (LEVELS[rec[`${ind.domainId}::${ind.id}`]?.level]?.score ?? 0) <= 1).length
                  const due    = reviewQueues[c._id]?.totalDue || 0
                  const age    = calcAge(c.dob)
                  const hasNextPlan = nextWeekPlans[c._id]
                  return (
                    <div key={c._id} onClick={() => openChild(c)}
                      style={{ background:'white', borderRadius:12, border:'1.5px solid #E4ECF5', padding:'14px 16px', cursor:'pointer', transition:'box-shadow .15s, transform .12s' }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow='0 6px 20px rgba(0,40,100,.09)'; e.currentTarget.style.transform='translateY(-1px)' }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow=''; e.currentTarget.style.transform='' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:11 }}>
                        <Avatar name={c.name} color={c.color||'#1A4A6E'} size={44} ring={op} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:800, fontSize:14, color:'#1A2E4A', marginBottom:4 }}>{c.name}</div>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                            {age != null && <Pill label={`Age ${age}`} color="#7A95B2" size={9} />}
                            {(c.disabilities||[]).slice(0,2).map(d => <Pill key={d} label={d} color="#1A4A6E" bg="#EBF4FB" size={9} />)}
                            {due > 0 && <Pill label={`⏰ ${due} due`} color="#E65100" bg="#FFF4EC" size={9} />}
                            {/* Next-week plan indicator */}
                            {!nextWeekLoading && hasNextPlan === false && (
                              <Pill label="📋 No next week plan" color="#B45309" bg="#FEF3C7" size={9} />
                            )}
                            {!nextWeekLoading && hasNextPlan === true && (
                              <Pill label="📅 Next week planned" color="#1B6B45" bg="#F0FFF4" size={9} />
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:24, fontWeight:900, color:c.color||'#1A4A6E', lineHeight:1 }}>{op}%</div>
                          <div style={{ fontSize:9, color:'#8FA5BC', marginTop:2 }}>overall</div>
                        </div>
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6, marginBottom:10 }}>
                        {DOMAINS.map(d => {
                          const pct = domainPct(rec, d.id)
                          return (
                            <div key={d.id}>
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:3 }}>
                                <span style={{ fontSize:9 }}>{d.icon}</span>
                                <span style={{ fontSize:9, fontWeight:700, color:d.color }}>{pct}%</span>
                              </div>
                              <Bar pct={pct} color={d.color} h={4} />
                            </div>
                          )
                        })}
                      </div>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div style={{ fontSize:10, color:'#8FA5BC' }}>
                          Last session: <strong style={{ color:'#3C4F66' }}>{relDate(lastS)}</strong>
                          {weakCt > 0 && <span style={{ marginLeft:10, color:'#C0392B', fontWeight:700 }}>⚠️ {weakCt} indicators</span>}
                        </div>
                        <div style={{ display:'flex', gap:6 }}>
                          <button onClick={e => { e.stopPropagation(); setReportChild(c) }}
                            style={{ padding:'4px 10px', fontSize:10, fontWeight:700, background:'#F0F5FC', border:'1px solid #D9E4F0', borderRadius:6, cursor:'pointer', color:'#3C4F66' }}>
                            📄 Report
                          </button>
                          <button onClick={e => { e.stopPropagation(); onNav('progress') }}
                            style={{ padding:'4px 10px', fontSize:10, fontWeight:700, background:'#1A2E4A', border:'none', borderRadius:6, cursor:'pointer', color:'white' }}>
                            📊 Progress
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Right sidebar */}
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ background:'white', borderRadius:13, padding:'17px 18px', border:'1.5px solid #E4ECF5' }}>
              <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:15, fontWeight:800, color:'#1A2E4A', marginBottom:4 }}>Class NCF Averages</div>
              <div style={{ fontSize:11, color:'#8FA5BC', marginBottom:14 }}>All assigned children</div>
              {classAvg.map(d => (
                <div key={d.id} style={{ marginBottom:11 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:4 }}>
                    <span style={{ fontSize:13 }}>{d.icon}</span>
                    <span style={{ flex:1, fontSize:11.5, fontWeight:600, color:'#3C4F66' }}>{d.short}</span>
                    <span style={{ fontSize:12, fontWeight:900, color:d.color }}>{d.avg}%</span>
                  </div>
                  <Bar pct={d.avg} color={d.color} h={5} />
                </div>
              ))}
              <div style={{ marginTop:14, borderTop:'1px solid #EEF3FA', paddingTop:13 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:'#7A95B2' }}>Class Overall</span>
                  <span style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:24, fontWeight:900, color:'#1A2E4A' }}>{classOverall}%</span>
                </div>
                <Bar pct={classOverall} color="#1A2E4A" h={7} />
              </div>
            </div>

            {needAttention.length > 0 && (
              <div style={{ background:'#FEF0EE', borderRadius:13, padding:'15px 16px', border:'1.5px solid #F5B7B1' }}>
                <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:14, fontWeight:800, color:'#922B21', marginBottom:4 }}>Needs Attention</div>
                <div style={{ fontSize:11, color:'#C0392B', marginBottom:12 }}>{needAttention.length} children below 50%</div>
                {needAttention.map(c => (
                  <div key={c._id} onClick={() => openChild(c)} style={{ display:'flex', alignItems:'center', gap:9, padding:'8px 10px', background:'white', borderRadius:9, marginBottom:7, cursor:'pointer', border:'1px solid #F5B7B1' }}>
                    <Avatar name={c.name} color={c.color||'#C0392B'} size={30} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:'#1A2E4A', marginBottom:3 }}>{c.name}</div>
                      <Bar pct={c.op} color="#C0392B" h={4} />
                    </div>
                    <span style={{ fontSize:13, fontWeight:900, color:'#C0392B' }}>{c.op}%</span>
                  </div>
                ))}
              </div>
            )}

            {/* Quick actions */}
            <div style={{ background:'white', borderRadius:13, padding:'15px 16px', border:'1.5px solid #E4ECF5' }}>
              <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:15, fontWeight:800, color:'#1A2E4A', marginBottom:12 }}>Quick Actions</div>
              {[
                { icon:'⏰', label:'SM-2 Schedule',     action:() => setActiveTab('schedule'), badge: totalDueAll > 0 ? totalDueAll : null },
                { icon:'📊', label:'Update Progress',   action:() => onNav('progress')  },
                { icon:'🎮', label:'Learning Activity', action:() => onNav('learn')     },
                { icon:'🃏', label:'Flashcard Session', action:() => onNav('flashcard') },
                {
                  icon:'📅',
                  label:'Weekly Work Plan',
                  action:() => { sessionStorage.setItem('workplan_target_week', getNextMondayISO()); onNav('workplan') },
                  badge: childrenWithoutNextPlan.length > 0 ? `${childrenWithoutNextPlan.length} need plan` : null,
                  badgeColor: '#B45309', badgeBg: '#FEF3C7',
                },
                { icon:'👤', label:'Manage Children',   action:() => onNav('children')  },
              ].map((a, i) => (
                <button key={i} onClick={a.action}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:9, padding:'8px 11px', marginBottom:5,
                           background:'#F8FAFD', border:'none', borderRadius:8, cursor:'pointer', transition:'background .1s' }}
                  onMouseEnter={e => e.currentTarget.style.background='#EEF3FA'}
                  onMouseLeave={e => e.currentTarget.style.background='#F8FAFD'}>
                  <span style={{ fontSize:14 }}>{a.icon}</span>
                  <span style={{ fontSize:12, fontWeight:600, color:'#3C4F66', flex:1, textAlign:'left' }}>{a.label}</span>
                  {a.badge && (
                    <span style={{ background: a.badgeBg || '#E65100', color: a.badgeColor || 'white', fontSize:9, fontWeight:900, borderRadius:10, padding:'1px 7px' }}>
                      {a.badge}
                    </span>
                  )}
                  <span style={{ fontSize:11, color:'#9EB0C4' }}>→</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ SCHEDULE TAB ══ */}
      {activeTab === 'schedule' && (
        <ScheduleTab children={children} sessions={sessions} records={records} reviewQueues={reviewQueues} algoResults={algoResults} currentPlan={currentPlan} todayDayIdx={todayDayIdx} onNav={onNav} onOpenChild={openChild} />
      )}

      {/* ══ HEATMAP TAB ══ */}
      {activeTab === 'heatmap' && (
        <div style={{ background:'white', borderRadius:14, padding:'20px 22px', border:'1.5px solid #E4ECF5' }}>
          <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:16, fontWeight:800, color:'#1A2E4A', marginBottom:4 }}>Class Skills Heatmap</div>
          <div style={{ fontSize:11, color:'#8FA5BC', marginBottom:16 }}>All indicators across all assigned children</div>
          {children.length === 0 ? <EmptyState icon="🔥" text="No children to display" /> : (
            <ClassHeatmap children={children} records={records} />
          )}
        </div>
      )}

      {/* ══ PLANNER / HISTORY TAB ══ */}
      {activeTab === 'planner' && (() => {
        const allSessRaw = children
          .flatMap(c => (sessions[c._id]||[]).map(s => ({ ...s, childName:c.name, childColor:c.color||'#1A4A6E' })))
          .sort((a,b) => new Date(b.createdAt)-new Date(a.createdAt))
        const allSrt = children
          .flatMap(c => (srtSessions[c._id]||[]).map(s => ({ ...s, childName:c.name, childColor:c.color||'#1A4A6E' })))
          .sort((a,b) => new Date(b.date)-new Date(a.date)).slice(0,20)

        // Session counts by conductor role
        const totalTeacher = allSessRaw.filter(s=>s.conductorRole!=='parent').length
        const totalParent  = allSessRaw.filter(s=>s.conductorRole==='parent').length
        const weekStart = new Date(); weekStart.setDate(weekStart.getDate()-weekStart.getDay()+(weekStart.getDay()===0?-6:1)); weekStart.setHours(0,0,0,0)
        const thisWeekTeacher = allSessRaw.filter(s=>s.conductorRole!=='parent'&&new Date(s.createdAt)>=weekStart).length
        const thisWeekParent  = allSessRaw.filter(s=>s.conductorRole==='parent'&&new Date(s.createdAt)>=weekStart).length

        return (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {/* ── Home vs School summary ── */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
              {[
                { label:'All Sessions',       val:allSessRaw.length,  icon:'📅', sub:`${allSessRaw.slice(0,30).length} shown`,                color:'#1A2E4A', bg:'white' },
                { label:'School Sessions',     val:totalTeacher,       icon:'🏫', sub:`${thisWeekTeacher} this week`,                          color:'#1A4A6E', bg:'white' },
                { label:'Home Practice',       val:totalParent,        icon:'🏠', sub:`${thisWeekParent} this week`,                           color:'#7B2D8B', bg:'white' },
              ].map((s,i) => (
                <div key={i} style={{ background:s.bg, borderRadius:12, padding:'14px 16px', border:'1.5px solid #E4ECF5', textAlign:'center' }}>
                  <div style={{ fontSize:20, marginBottom:4 }}>{s.icon}</div>
                  <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:22, fontWeight:900, color:s.color, lineHeight:1 }}>{s.val}</div>
                  <div style={{ fontSize:11, fontWeight:700, color:'#3C4F66', marginTop:3 }}>{s.label}</div>
                  <div style={{ fontSize:10, color:'#8FA5BC', marginTop:2 }}>{s.sub}</div>
                </div>
              ))}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
              {/* Session list with filter */}
              <SessionHistoryList allSess={allSessRaw} />
            <div style={{ background:'white', borderRadius:14, padding:'19px 21px', border:'1.5px solid #E4ECF5' }}>
              <div style={{ fontFamily:"'Playfair Display',Georgia,serif", fontSize:15, fontWeight:800, color:'#1A2E4A', marginBottom:4 }}>SRT History</div>
              <div style={{ fontSize:11, color:'#8FA5BC', marginBottom:14 }}>School Readiness Tests · all children</div>
              {allSrt.length === 0 ? <EmptyState icon="🎮" text="No SRT sessions yet" /> : (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {allSrt.map(s => {
                    const total = Object.keys(s.responses||{}).length
                    const ach   = Object.values(s.responses||{}).filter(r=>r.level==='achieved').length
                    const pct   = total ? Math.round(ach/total*100) : 0
                    return (
                      <div key={s._id} style={{ display:'flex', alignItems:'center', gap:10, background:'#F8FAFD', borderRadius:8, padding:'9px 12px' }}>
                        <span style={{ fontSize:16, flexShrink:0 }}>🎮</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:12, fontWeight:700, color:'#1A2E4A' }}>School Readiness Test</div>
                          <div style={{ fontSize:10, color:'#8FA5BC' }}><strong style={{ color:s.childColor }}>{s.childName}</strong> · {relDate(s.date)}</div>
                        </div>
                        <Pill label={`${pct}%`} color={accColor(pct)} bg={accBg(pct)} size={10} />
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div> </div>
        )
      })()}

      {/* ── Child Drawer ── */}
      {selectedChild && (
        <ChildDrawer child={selectedChild} record={records[selectedChild._id] || {}} srtSessions={srtSessions[selectedChild._id] || []}
          algData={algData[selectedChild._id]} algoResults={algoResults[selectedChild._id]||[]} onClose={() => setSelectedChild(null)} onNav={onNav} />
      )}

      {/* ── IEP Modal ── */}
      {reportChild && (
        <IEPModal child={reportChild} record={records[reportChild._id] || {}} onClose={() => setReportChild(null)} />
      )}

      <style>{`
        @keyframes td-spin    { to { transform: rotate(360deg) } }
        @keyframes td-slideIn { from { transform: translateX(40px); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
      `}</style>
    </div>
  )
}